import { useEffect, useState, useCallback } from 'react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'caldeira-theme';
const CLASS_MAP: Record<Theme, string | null> = {
  light: null,
  dark: 'theme-dark',
};

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Migrate old 'graphite' value to 'dark'
    if (stored === 'graphite') {
      localStorage.setItem(STORAGE_KEY, 'dark');
      return 'dark';
    }
    if (stored === 'dark') return 'dark';
  } catch {}
  return 'light';
}

function applyTheme(theme: Theme) {
  const body = document.body;
  body.classList.remove('theme-dark');
  const cls = CLASS_MAP[theme];
  if (cls) body.classList.add(cls);
}

// Apply on load before React hydrates
applyTheme(getStoredTheme());

const listeners = new Set<(t: Theme) => void>();

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
    applyTheme(t);
    listeners.forEach(fn => fn(t));
  }, []);

  useEffect(() => {
    const handler = (t: Theme) => setThemeState(t);
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return { theme, setTheme } as const;
}
